import React, { useState } from 'react';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  ContentTemplate,
  Divider,
  Grid,
  HtmlTrans,
  Progress,
  SectionHeader,
  TitleHeader,
  Typography,
} from 'web-library';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, FieldValues } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import * as Joi from 'joi';
import RouteKey from '../../routes/key';
import { candidateService } from '../../api';
import ControlledAutocomplete from '../../components/form-items/ControlledAutocomplete';
import {
  ExamLevelObject,
  useGetCoordinatorAppealExamLevel,
} from '../../api-refactored/appeal/useGetCoordinatorAppealExamLevel';
import { usePostCoordinatorAppealCheckEligibility } from '../../api-refactored/appeal/usePostCoordinatorAppealCheckEligibility';
import { getJoiObject } from '../../utils/JoiUtils';
import { LookupListApiItem } from '../../api/user/model';
import { parseLocationSearch } from '../../utils/StringUtils';

const { appeal, error, uniqueLink } = RouteKey.child;
const { selectCandidate, applicationsCoordinator } = RouteKey.appeal.child;
const { invalid, generic } = RouteKey.error.child;

export const useStyles = makeStyles()((theme) =>
({
  notesContainer: {
    paddingBottom: theme.spacing(5),
  },
  examLevelTable: {
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
  },
  examLevelSelect: {
    paddingBottom: theme.spacing(3),
  },
  startAppealButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(7),
    height: 'fit-content',
  },
  feeContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: 'red',
  },
  errorText: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  feeColumnHeader: {
    textAlign: 'right',
  },
}));

const examLevelSchema = Joi.object({
  examLevelDropdown: getJoiObject(
    Joi.object().required().messages({
      'any.required': 'Please select Exam Level',
      'object.base': 'Please select Exam Level',
    })
  ),
});

interface CoordinatorAppealNotesProps {
  title: string;
}

const CoordinatorAppealNotes = ({ title }: CoordinatorAppealNotesProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const location = useLocation();

  const { token = '' } = parseLocationSearch(location.search);

  const [errMsg, setErrMsg] = useState<string>('');

  const [examCodeDescriptionToken, setExamCodeDescriptionToken] = useState<{
    examCode: string;
    examDescription: string;
    token: string | null;
  }>({ examCode: '', examDescription: '', token: null });

  const {
    data: appealFeesData,
    isLoading: isLoadingAppealFeesData,
  } = candidateService.appeal.candidate.useGetAppealFees();

  const {
    data: examLevelData,
    isLoading: isLoadingExamLevelData,
  } = useGetCoordinatorAppealExamLevel();

  const {
    mutate: mutatePostCheckEligibility,
    status: statusCheckEligibility,
  } = usePostCoordinatorAppealCheckEligibility({
    onSuccess: () =>
      history.push(`/${appeal}/${applicationsCoordinator.route}/${selectCandidate.route}`, {
        examCodeDescriptionToken,
      }),
    onError: (err) => {
      const data = err?.response?.data;

      if (data) {
        const { code, message } = data;
        if (code === '1005') {
          setErrMsg(t('appeal->>AppealCoordinatorNotes.NoCandidateWithResults'));
        }
        if (code === '1007') {
          setErrMsg(message!);
        }
        if (code === '1003' || code === '1014' || code === '1009') {
          history.push(`/${uniqueLink}/${invalid}`);
        }
        if (code === '1203') {
          history.push(`/${error}/${generic}`);
        }
      }
    },
  });

  const { formState: { errors }, control, handleSubmit } = useForm<FieldValues>({
    mode: 'onSubmit',
    resolver: joiResolver(examLevelSchema),
  });

  return (
    <form
      onSubmit={handleSubmit(async (formData) => {
        await setExamCodeDescriptionToken({
          examCode: formData.examLevelDropdown.code,
          examDescription: formData.examLevelDropdown.description,
          token: token || null,
        });
        mutatePostCheckEligibility({
          examCode: formData.examLevelDropdown.code,
          token: token || null,
        });
      })}
    >
      <TitleHeader title={title} />
      {isLoadingAppealFeesData || isLoadingExamLevelData ? (
        <ContentTemplate>
          <Progress color="secondary" />
        </ContentTemplate>
      ) : (
        <ContentTemplate>
          {appealFeesData && (
            <>
              <Grid>
                <div className={classes.notesContainer}>
                  <SectionHeader
                    title={t('appeal->>AppealCoordinatorNotes.ImportantNotesForCoordinators')}
                  />
                  <ul>
                    <li>
                      <Typography variant="body1">
                        {t('appeal->>AppealCoordinatorNotes.AppealNotesStatementOne')}
                      </Typography>
                      <br />
                      <Typography variant="body1">
                        {t('appeal->>AppealNotes.CandidateAppealNotesStatementTwo')}
                      </Typography>
                    </li>
                    <br />
                    <li>
                      <Typography>
                        {t('appeal->>AppealNotes.CandidateAppealNotesStatementThree')}
                      </Typography>
                    </li>
                  </ul>
                  <ul>
                    <li>
                      <Typography>
                        {t('appeal->>AppealCoordinatorNotes.AppealNotesStatementFour')}
                      </Typography>
                    </li>
                  </ul>
                  <Typography variant="body1">
                    <HtmlTrans i18nKey="appeal->>AppealNotes.CandidateAppealNotesNote" />
                  </Typography>
                </div>
                <Divider />
                <SectionHeader title={t('appeal->>AppealNotes.Get Started')} />
                <Grid className={classes.examLevelSelect} xs={12}>
                  <ControlledAutocomplete
                    // className={classes.inputSelect}
                    id="input-select-exam-level"
                    name="examLevelDropdown"
                    control={control}
                    label={t('appeal->>AppealCoordinatorNotes.SelectExamLevel')}
                    placeholder={t('appeal->>AppealCoordinatorNotes.SelectExamLevel')}
                    options={examLevelData || []}
                    getOptionLabel={(option: string | ExamLevelObject) => typeof option === 'string' ? option : option.description}
                    isOptionEqualToValue={(option: ExamLevelObject, value: ExamLevelObject) => `${option.code}` === `${value.code}`}
                    error={!!errors?.examLevelDropdown}
                    helperText={errors?.examLevelDropdown?.message?.toString()}
                    disableClearable
                    readOnly
                  />
                </Grid>
              </Grid>
              {errMsg && (
                <Grid xs={12} md={8}>
                  <Typography className={classes.errorText} display="inline"
                    variant="body2">
                    {errMsg}
                  </Typography>
                </Grid>
              )}
              <Grid xs={12} sm={5} md={4}>
                <ApiButton
                  status={statusCheckEligibility}
                  className={classes.startAppealButton}
                  fullWidth
                  id="button-appealImportantNotes-start"
                >
                  {t('appeal->>AppealCoordinatorNotes.StartANewAppeal')}
                </ApiButton>
              </Grid>
            </>
          )}
        </ContentTemplate>
      )}
    </form>
  );
};

export default CoordinatorAppealNotes;
